import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { Lock, Mail, ArrowRight, ShieldCheck, CheckCircle2, Zap, LineChart, User, Phone, Eye, EyeOff, KeyRound } from 'lucide-react';

const Signup = () => {
  const navigate = useNavigate();
  const { user, loading, signUp, verifyOtp, signOut } = useAuth();
  
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');

  const calculateStrength = (pwd) => {
    let score = 0;
    if (!pwd) return { score: 0, label: '', color: 'bg-slate-200' };
    
    const hasLower = /[a-z]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSymbol = /[^A-Za-z0-9]/.test(pwd);
    const hasLength = pwd.length >= 8;

    if (hasLower) score += 1;
    if (hasUpper) score += 1;
    if (hasNumber) score += 1;
    if (hasSymbol) score += 1;
    if (hasLength) score += 1;
    
    if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score === 3 || score === 4) return { score, label: 'Fair', color: 'bg-orange-500' };
    if (score === 5) return { score, label: 'Strong', color: 'bg-emerald-500' };
    return { score: 0, label: '', color: 'bg-slate-200' };
  };

  const strength = calculateStrength(password);

  // Prevent redirecting if they are in the OTP flow or looking at the success message
  if (!loading && user && !success && !showOtpInput) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (strength.score < 5) {
      setError('Password must contain at least 8 characters, including uppercase, lowercase, numbers, and special symbols');
      return;
    }
    
    setSubmitting(true);
    try {
      await signUp({ 
        email, 
        password,
        options: {
          data: {
            display_name: displayName,
            phone: phone
          }
        }
      });
      
      setShowOtpInput(true);
      setError('');
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await verifyOtp({ email, token: otp, type: 'signup' });
      await signOut(); // Prevent direct login, force manual login
      setShowOtpInput(false);
      setSuccess('Account verified successfully! Please log in with your new credentials.');
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-yellow-50 py-12">
      
      {/* Subtle Background Elements */}
      <div className="fixed top-0 left-1/4 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none mix-blend-multiply"></div>
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-[150px] pointer-events-none mix-blend-multiply"></div>

      {/* Floating Feature Elements (Background) */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden hidden md:block">
        {/* Top Left Feature */}
        <div className="absolute top-20 left-[10%] md:left-[20%] bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-200/50 shadow-sm transform -rotate-6 animate-pulse" style={{ animationDuration: '4s' }}>
          <div className="flex items-center gap-3 opacity-70">
            <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Zap className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-800">Smart Validation</div>
              <div className="text-[10px] text-slate-500">Error-free AWB entries</div>
            </div>
          </div>
        </div>

        {/* Bottom Left Feature */}
        <div className="absolute bottom-32 left-[5%] md:left-[15%] bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-200/50 shadow-sm transform rotate-12 animate-pulse" style={{ animationDuration: '6s', animationDelay: '1s' }}>
          <div className="flex items-center gap-3 opacity-70">
            <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-800">Auto GST Split</div>
              <div className="text-[10px] text-slate-500">Real-time calculations</div>
            </div>
          </div>
        </div>

        {/* Top Right Feature */}
        <div className="absolute top-40 right-[5%] md:right-[15%] bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-200/50 shadow-sm transform rotate-6 animate-pulse" style={{ animationDuration: '5s', animationDelay: '2s' }}>
          <div className="flex items-center gap-3 opacity-70">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <LineChart className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-800">Live Reports</div>
              <div className="text-[10px] text-slate-500">Track every penny</div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Top Accent Line */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-indigo-600 to-orange-500"></div>

      <div className="relative z-10 w-full max-w-xl px-6">
        
        {/* Logo/Brand Header */}
        <div className="flex flex-col items-center mb-5">
          <div 
              className="flex items-center cursor-pointer group mb-1" 
              onClick={() => navigate('/')}
          >
              <span className="text-3xl font-black tracking-tight text-indigo-900 transition-colors group-hover:text-indigo-700">FR-Bill</span>
              <span className="text-3xl font-black tracking-tight text-orange-500 transition-colors group-hover:text-orange-600">Net</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-px w-6 bg-slate-300"></span>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-[0.2em]">New Account</p>
            <span className="h-px w-6 bg-slate-300"></span>
          </div>
        </div>

        {/* Signup Card */}
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-black text-slate-900">
                Create Account
              </h2>
            </div>
            
            {error && (
              <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm font-medium flex items-start gap-3">
                <div className="w-1 h-5 mt-0.5 bg-red-500 rounded-full shrink-0"></div>
                {error}
              </div>
            )}

            {success && (
              <div className="mb-5 p-6 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm flex flex-col items-center gap-3 text-center">
                <CheckCircle2 size={36} className="text-emerald-500 mb-2" />
                <p className="font-medium">{success}</p>
                <button 
                  onClick={() => navigate('/login')}
                  className="mt-4 text-white bg-indigo-600 hover:bg-indigo-700 px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest shadow-sm transition-colors"
                >
                  Go to Login
                </button>
              </div>
            )}

            {showOtpInput && !success && (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="mb-4 p-4 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-700 text-sm text-center">
                  <p className="font-medium">Please enter the 6-digit verification code sent to <strong>{email}</strong></p>
                </div>
                
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Verification Code</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                      <KeyRound size={20} />
                    </div>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                      required
                      maxLength={6}
                      className="block w-full pl-12 pr-4 py-3.5 bg-yellow-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-bold text-center tracking-[0.5em] text-xl"
                      placeholder="000000"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || otp.length < 6}
                  className="w-full mt-4 flex items-center justify-center gap-2 py-4 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm hover:shadow-md transform transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed text-sm"
                >
                  {submitting ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    <>
                      <span>Verify Account</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            )}

            {!success && !showOtpInput && (
              <form onSubmit={onSubmit} className="space-y-4">
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Display Name</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                        <User size={16} />
                      </div>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        required
                        className="block w-full pl-10 pr-3 py-2.5 bg-yellow-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-sm"
                        placeholder="Jane Doe"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Phone Number</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                        <Phone size={16} />
                      </div>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        className="block w-full pl-10 pr-3 py-2.5 bg-yellow-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-sm"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Email Address</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                      <Mail size={16} />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="block w-full pl-10 pr-3 py-2.5 bg-yellow-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-sm"
                      placeholder="name@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Password</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                        <Lock size={16} />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="block w-full pl-10 pr-10 py-2.5 bg-yellow-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-sm"
                        placeholder="••••••••"
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-indigo-600 transition-colors"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {/* Password Strength Indicator */}
                    {password && (
                      <div className="mt-2 px-1">
                        <div className="flex gap-1 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${strength.score >= 1 ? strength.color : 'bg-transparent'} transition-all duration-300`} style={{ width: '20%' }}></div>
                          <div className={`h-full ${strength.score >= 2 ? strength.color : 'bg-transparent'} transition-all duration-300`} style={{ width: '20%' }}></div>
                          <div className={`h-full ${strength.score >= 3 ? strength.color : 'bg-transparent'} transition-all duration-300`} style={{ width: '20%' }}></div>
                          <div className={`h-full ${strength.score >= 4 ? strength.color : 'bg-transparent'} transition-all duration-300`} style={{ width: '20%' }}></div>
                          <div className={`h-full ${strength.score >= 5 ? strength.color : 'bg-transparent'} transition-all duration-300`} style={{ width: '20%' }}></div>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-[9px] text-slate-400 font-medium">Use 8+ chars, upper, lower, num & symbol</p>
                          <p className={`text-[10px] font-bold ${strength.score <= 2 ? 'text-red-500' : strength.score < 5 ? 'text-orange-500' : 'text-emerald-500'}`}>
                            {strength.label}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Confirm Password</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                        <Lock size={16} />
                      </div>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="block w-full pl-10 pr-10 py-2.5 bg-yellow-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-sm"
                        placeholder="••••••••"
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-indigo-600 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-4 flex items-center justify-center gap-2 py-3.5 px-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl shadow-sm hover:shadow-md transform transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed text-sm"
                >
                  {submitting ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Creating Account...</span>
                    </div>
                  ) : (
                    <>
                      <span>Complete Sign Up</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Footer Sign In Link */}
          {!success && !showOtpInput && (
            <div className="bg-yellow-50 p-4 border-t border-slate-100 flex items-center justify-center text-sm">
              <p className="text-slate-500 font-medium">
                Already have an account?{' '}
                <button onClick={() => navigate('/login')} className="text-indigo-600 hover:text-indigo-800 font-bold ml-1">
                  Sign In
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;
